/**
 * Signing a representative in, and holding the result in memory.
 *
 * The whole of S8.8. Discovery, the redirect out, the code exchange on the way back, and a token that lives
 * in a module-scoped variable and nowhere else.
 *
 * ## Why nothing is persisted
 *
 * A reload signs in again rather than reading a credential off the disk. That costs a redirect the
 * representative barely sees - the provider's own session cookie is first-party to it, so there is no second
 * password prompt - and it buys the property that this origin's storage never holds anything that grants
 * access to a document. The rejected alternative was a refresh token in `localStorage`, which any script on
 * this origin can read, for the sake of avoiding that redirect.
 *
 * A refresh token *is* kept, in memory, because the realm gives an access token five minutes and a
 * representative should not be interrupted mid-task every five minutes. It dies with the tab.
 *
 * ## What this is not
 *
 * Not client sign-in. D-21 asks whether client logins share the representative tenant and is open, so this
 * authenticates representatives only, and X9 is where the other half goes once that is answered.
 */
import {
  authorizationUrl,
  callbackParams,
  challengeFor,
  randomState,
  randomVerifier,
  rememberPending,
  takePending,
} from './pkce';

export interface IdentityConfig {
  readonly issuer: string;
  readonly clientId: string;
  /** Space-separated scopes, from `config.json`. See `DEFAULT_IDENTITY_SCOPE` in `config.ts`. */
  readonly scope: string;
}

interface Discovery {
  readonly authorization_endpoint: string;
  readonly token_endpoint: string;
  readonly end_session_endpoint?: string;
}

/**
 * The tokens, in memory.
 *
 * Module scope rather than React state, for the same reason the widget keeps its token in a ref: a request in
 * flight must be able to read the current value, and a refresh must not re-render the tree and unmount
 * whatever is holding that request.
 */
let accessToken: string | undefined;
let refreshToken: string | undefined;
let discovered: Discovery | undefined;

/** Cleared on sign-out and after a failed refresh. Nothing else touches these. */
function forget(): void {
  accessToken = undefined;
  refreshToken = undefined;
}

export function currentToken(): string | undefined {
  return accessToken;
}

export function signedIn(): boolean {
  return accessToken !== undefined;
}

async function discovery(issuer: string): Promise<Discovery> {
  if (discovered !== undefined) return discovered;

  // RFC 8414 appends the well-known path to the issuer. The trailing slash is trimmed because an issuer that
  // ends in one would otherwise produce a double slash that some providers 404.
  const response = await fetch(`${issuer.replace(/\/+$/, '')}/.well-known/openid-configuration`);
  if (!response.ok) {
    throw new Error(`The identity provider at ${issuer} did not answer (${response.status}).`);
  }
  discovered = (await response.json()) as Discovery;
  return discovered;
}

/** Where the provider sends the browser back. Registered in the realm, so it must match exactly. */
function redirectUri(): string {
  return `${globalThis.location.origin}/callback`;
}

/**
 * Start the flow. Navigates away, so it never returns.
 *
 * `returnTo` is remembered alongside the verifier so a deep link survives the round trip: a representative
 * who opened a link to one exchange lands back on that exchange rather than on the directory.
 */
export async function beginSignIn(config: IdentityConfig, returnTo: string = globalThis.location.pathname): Promise<void> {
  const { authorization_endpoint: endpoint } = await discovery(config.issuer);

  const verifier = randomVerifier();
  const state = randomState();
  rememberPending({ state, verifier, returnTo });

  globalThis.location.assign(
    authorizationUrl({
      authorizationEndpoint: endpoint,
      clientId: config.clientId,
      redirectUri: redirectUri(),
      state,
      challenge: await challengeFor(verifier),
      scope: config.scope,
    }),
  );
}

export type CallbackResult =
  | { readonly kind: 'signed_in'; readonly returnTo: string }
  | { readonly kind: 'not_a_callback' }
  | { readonly kind: 'failed'; readonly detail: string };

/**
 * Finish the flow, if this page load is a callback.
 *
 * Returns `not_a_callback` for an ordinary load, which is what makes it safe to call unconditionally on
 * mount. Every failure below is a refusal rather than a retry: a callback that cannot be completed is either
 * a stale tab or somebody feeding us a code, and the answer to both is to start again deliberately.
 */
export async function completeSignIn(config: IdentityConfig): Promise<CallbackResult> {
  const params = callbackParams(globalThis.location.href);
  if (params === undefined) return { kind: 'not_a_callback' };

  // Taken unconditionally, so a failed callback cannot leave a verifier behind for a second attempt.
  const pending = takePending();

  if (params.error !== undefined) {
    // The provider's own slug - `access_denied`, `login_required`. Shown rather than translated, because it
    // is what a customer's identity administrator will search for.
    return { kind: 'failed', detail: `The identity provider refused the sign-in (${params.error}).` };
  }
  if (pending === undefined) {
    return { kind: 'failed', detail: 'This sign-in did not start in this tab. Try again.' };
  }
  if (params.state !== pending.state) {
    // The CSRF control. A mismatch means this callback did not come from the request this tab started.
    return { kind: 'failed', detail: 'The sign-in could not be verified. Try again.' };
  }
  if (params.code === undefined) {
    return { kind: 'failed', detail: 'The identity provider returned no authorization code.' };
  }

  const { token_endpoint: endpoint } = await discovery(config.issuer);
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      code: params.code,
      redirect_uri: redirectUri(),
      client_id: config.clientId,
      // No client secret. A public client has none, and PKCE is what stands in its place.
      code_verifier: pending.verifier,
    }),
  });

  if (!response.ok) {
    return { kind: 'failed', detail: `The authorization code could not be exchanged (${response.status}).` };
  }

  const tokens = (await response.json()) as { access_token: string; refresh_token?: string };
  accessToken = tokens.access_token;
  refreshToken = tokens.refresh_token;

  return { kind: 'signed_in', returnTo: pending.returnTo };
}

/**
 * A fresh access token from the refresh token held in memory.
 *
 * Returns undefined when the session cannot be revived, which sends the representative back to the sign-in
 * screen. Called by the API client on a 401, which on a five-minute access token is the common case rather
 * than the exceptional one.
 */
export async function refreshAccessToken(config: IdentityConfig): Promise<string | undefined> {
  if (refreshToken === undefined) return undefined;

  const { token_endpoint: endpoint } = await discovery(config.issuer);
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
      client_id: config.clientId,
      // Sent on the refresh too, and it is not redundant. Entra scopes a refresh to the resource being
      // asked for: omit it and the new access token is audienced for whatever it defaults to rather than
      // for our API, so the refresh appears to succeed and every call after it answers 401. Keycloak
      // ignores it, which is why this went unnoticed until the provider changed.
      scope: config.scope,
    }),
  });

  if (!response.ok) {
    // A refresh token the provider has rotated away, or a session that ended at the provider. Both mean this
    // session is over, and holding a dead token would make every later call fail in a way that looks like
    // our bug.
    forget();
    return undefined;
  }

  const tokens = (await response.json()) as { access_token: string; refresh_token?: string };
  accessToken = tokens.access_token;
  // Keycloak rotates the refresh token and invalidates the old one, so keeping the previous value would make
  // the *next* refresh fail rather than this one.
  if (tokens.refresh_token !== undefined) refreshToken = tokens.refresh_token;
  return accessToken;
}

/**
 * Sign out locally, and at the provider when it says how.
 *
 * Both halves matter and they are different things. Forgetting the token here ends this application's
 * session; the provider's `end_session_endpoint` ends the single sign-on session, without which the next
 * "Sign in" click would silently sign the same person straight back in - which is not what somebody handing
 * their laptop over meant.
 */
export async function signOut(config: IdentityConfig): Promise<void> {
  forget();

  const { end_session_endpoint: endSession } = await discovery(config.issuer);
  if (endSession === undefined) {
    globalThis.location.assign('/');
    return;
  }

  const url = new URL(endSession);
  url.searchParams.set('client_id', config.clientId);
  url.searchParams.set('post_logout_redirect_uri', globalThis.location.origin);
  globalThis.location.assign(url.toString());
}
